import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ContentCounts } from '../../types/chemistry';
import { MoleculeIcon, ReactionIcon, ProteinIcon, DataFileIcon } from '../icons/ContentIcons';
import { formatDate } from '../../utils/dateFormat';

interface ChemistryProjectCardProps {
  id: number;
  name: string;
  description?: string;
  color: string;
  contentCounts?: ContentCounts;
  lastModified?: string;
  patentRiskCount?: number;
  archived?: boolean;
  onClick: () => void;
  onArchive?: (id: number) => void;
  onRestore?: (id: number) => void;
  onPermanentDelete?: (id: number) => void;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}


const ChemistryProjectCard: React.FC<ChemistryProjectCardProps> = ({
  id,
  name,
  description,
  color,
  contentCounts,
  lastModified,
  patentRiskCount,
  archived,
  onClick,
  onArchive,
  onRestore,
  onPermanentDelete,
}) => {
  const counts = contentCounts || { molecules: 0, reactions: 0, proteins: 0, files: 0 };
  const risk = patentRiskCount ?? 0;
  const isDemo = name.startsWith('Sample:');

  const [showMenu, setShowMenu] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showMenu && !contextMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
      setContextMenu(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu, contextMenu]);

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  return (
    <div
      className={`chem-card${archived ? ' chem-card--archived' : ''}`}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      onContextMenu={(e) => { if (archived) return; e.preventDefault(); e.stopPropagation(); setContextMenu({ x: e.clientX, y: e.clientY }); }}
      role="link"
      tabIndex={0}
      style={archived ? { opacity: 0.65, filter: 'saturate(0.4)' } : undefined}
    >
      {/* Accent bar */}
      <div className="chem-card__accent" style={{ backgroundColor: color }} />

      {/* Row 1: Name + badges */}
      <div className="chem-card__header">
        <div className="chem-card__name-group">
          <h3 className="chem-card__name">{name}</h3>
          {isDemo && <span className="chem-card__badge chem-card__badge--demo">DEMO</span>}
          {archived && (
            <span className="chem-card__badge chem-card__badge--archived">Archived</span>
          )}
        </div>
        {/* patent count shown in stats row + footer — no badge here */}
      </div>

      {/* Description */}
      {description && <p className="chem-card__description">{description}</p>}

      {/* Row 2: Stats grid */}
      <div className="chem-card__stats">
        <Link
          to={`/chemistry/files?project=${id}`}
          className="chem-card__stat chem-card__stat--clickable"
          onClick={(e) => e.stopPropagation()}
          title="View compounds"
          style={{ textDecoration: 'none' }}
        >
          <span className="chem-card__stat-value">{formatCount(counts.molecules)}</span>
          <span className="chem-card__stat-label">
            <MoleculeIcon size={10} />
            COMPOUNDS
          </span>
        </Link>
        <Link
          to={`/chemistry/files?project=${id}&tab=documents`}
          className="chem-card__stat chem-card__stat--clickable"
          onClick={(e) => e.stopPropagation()}
          title="View all project documents and chemistry data files"
          style={{ textDecoration: 'none' }}
        >
          <span className="chem-card__stat-value">{formatCount(counts.files)}</span>
          <span className="chem-card__stat-label">
            <DataFileIcon size={10} />
            ALL FILES
          </span>
        </Link>
        <Link
          to="/chemistry/reactions"
          className="chem-card__stat chem-card__stat--clickable"
          onClick={(e) => e.stopPropagation()}
          title="View reactions"
          style={{ textDecoration: 'none' }}
        >
          <span className="chem-card__stat-value chem-card__stat-value--teal">{formatCount(counts.reactions)}</span>
          <span className="chem-card__stat-label">
            <ReactionIcon size={10} />
            REACTIONS
          </span>
        </Link>
        <Link
          to={`/chemistry/files?project=${id}`}
          className="chem-card__stat chem-card__stat--clickable"
          onClick={(e) => e.stopPropagation()}
          title="View patent hits"
          style={{ textDecoration: 'none' }}
        >
          <span className={`chem-card__stat-value ${risk > 10 ? 'chem-card__stat-value--amber' : ''}`}>
            {risk > 0 ? risk : '\u2014'}
          </span>
          <span className="chem-card__stat-label">
            <ProteinIcon size={10} />
            PATENT HITS
          </span>
        </Link>
        <Link
          to={`/chemistry/files?project=${id}`}
          className="chem-card__stat chem-card__stat--clickable"
          onClick={(e) => e.stopPropagation()}
          title="View patent filings"
          style={{ textDecoration: 'none' }}
        >
          <span className="chem-card__stat-value">{'\u2014'}</span>
          <span className="chem-card__stat-label">
            <ProteinIcon size={10} />
            PAT. FILED
          </span>
        </Link>
      </div>

      {/* Row 4: Footer */}
      <div className="chem-card__footer">
        {archived ? (
          <div className="chem-card__archived-actions" onClick={(e) => e.stopPropagation()}>
            {onRestore && (
              <button
                onClick={() => onRestore(id)}
                className="chem-card__action-btn chem-card__action-btn--restore"
              >
                Restore
              </button>
            )}
            {onPermanentDelete && (
              <button
                onClick={() => onPermanentDelete(id)}
                className="chem-card__action-btn chem-card__action-btn--delete"
              >
                Delete Forever
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="chem-card__patent-zones">
              {risk > 0 ? (
                <span className="chem-card__zone-summary">
                  <span className="chem-card__zone-dot chem-card__zone-dot--red" />
                  <span className="chem-card__zone-count">{risk} patent hit{risk !== 1 ? 's' : ''}</span>
                </span>
              ) : (
                <span className="chem-card__zone-none">No patent hits</span>
              )}
            </div>
            <span className="chem-card__timestamp">{formatDate(lastModified)}</span>
          </>
        )}
      </div>

      {/* Kebab menu — active cards only */}
      {onArchive && !archived && (
        <div
          onClick={handleMenuClick}
          className="chem-card__menu-btn"
          role="button"
          tabIndex={0}
        >
          <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
          </svg>
        </div>
      )}

      {/* Dropdown menu */}
      {showMenu && !archived && (
        <div
          ref={menuRef}
          className="chem-card__menu"
          onClick={(e) => e.stopPropagation()}
        >
          {onArchive && (
            <button
              onClick={() => {
                setShowMenu(false);
                onArchive(id);
              }}
              className="chem-card__menu-item"
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
              Archive
            </button>
          )}
        </div>
      )}
      {contextMenu && !archived && (
        <div
          className="chem-card__menu"
          style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y, right: 'auto', zIndex: 1000 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Link to={`/chemistry/files?project=${id}`} className="chem-card__menu-item" onClick={() => setContextMenu(null)} style={{ textDecoration: 'none' }}>Open project</Link>
          <a href={`/chemistry/files?project=${id}`} target="_blank" rel="noopener noreferrer" className="chem-card__menu-item" onClick={() => setContextMenu(null)} style={{ textDecoration: 'none' }}>Open in new tab</a>
          <button className="chem-card__menu-item" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/chemistry/files?project=${id}`); setContextMenu(null); }}>Copy link</button>
        </div>
      )}
    </div>
  );
};

export default ChemistryProjectCard;
